import { Fragment, memo, useId } from 'react';
import type { CSSProperties } from 'react';
import type { Era } from '../../art.js';
import { PALETTE } from '../kit.js';

/**
 * The archive's things: the objects the ARCHIVIST's games are drawn with (the pairs, the shadows,
 * the shelf). Each is a few bold shapes in a 100 × 100 box, lit from the top left, so it reads at
 * a glance on a phone at arm's length. A thing can be drawn lit (its own colours) or as a flat
 * silhouette; many have a variant, where one small part is different (a short spout for a long
 * one, a heel on the boot), and most face one way, so a mirror image is a different shadow.
 * `ThingSvg` nests in a board's own SVG (x, y, width, height in board units), so the five boards
 * draw their things in the kit's 320 × 320 space.
 */

export type Tone = 'paint' | 'metal' | 'brass' | 'wood' | 'glass' | 'dark' | 'leaf' | 'paper' | 'flame' | 'shine';

export interface Part {
  d: string;
  tone: Tone;
  /** A stroke this wide instead of a filled shape. */
  w?: number;
  /** Only in the base form (0) or only in the variant (1). */
  v?: 0 | 1;
  /** Drawn lit only: a detail the silhouette leaves out. */
  lit?: true;
  /** Holes cut with the even-odd rule. */
  eo?: true;
}

export interface ThingDef {
  en: string;
  no: string;
  /** Its own colour, for the parts in `paint`; none when it is all metal, brass or wood. */
  paint?: string;
  /** Drawn turned by this many degrees about the middle. */
  rot?: number;
  /** Where its lowest point is, when it is not about 90 (it lies flat, or floats). */
  foot?: number;
  /** Faces one way: its mirror image is a different shape. */
  asym: boolean;
  parts: readonly Part[];
}

const circle = (cx: number, cy: number, r: number) => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0Z`;
const ring = (cx: number, cy: number, r: number, hole: number) => `${circle(cx, cy, r)}${circle(cx, cy, hole)}`;

/** A cog: `teeth` teeth between radii `root` and `tip`, with a hole. */
function cog(cx: number, cy: number, teeth: number, root: number, tip: number, hole: number): string {
  const pts: string[] = [];
  const step = (Math.PI * 2) / teeth;
  const at = (a: number, r: number) => `${(cx + Math.cos(a) * r).toFixed(1)} ${(cy + Math.sin(a) * r).toFixed(1)}`;
  for (let i = 0; i < teeth; i++) {
    const a = i * step - Math.PI / 2;
    pts.push(at(a, root), at(a + step * 0.14, tip), at(a + step * 0.46, tip), at(a + step * 0.6, root));
  }
  return `M${pts.join('L')}Z${circle(cx, cy, hole)}`;
}

/** Petals round a centre. */
function petals(cx: number, cy: number, n: number, dist: number, r: number): string {
  let d = '';
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    d += circle(+(cx + Math.cos(a) * dist).toFixed(1), +(cy + Math.sin(a) * dist).toFixed(1), r);
  }
  return d;
}

export const THINGS = {
  key: {
    en: 'key',
    no: 'nøkkel',
    foot: 75,
    asym: true,
    parts: [
      { d: ring(25, 50, 15, 7), tone: 'brass', eo: true },
      { d: 'M40 41h7v18h-7Z', tone: 'brass' },
      { d: 'M46 45.5h40a4.5 4.5 0 0 1 0 9h-40Z', tone: 'brass' },
      { d: 'M68 54h18v20h-6v-9h-5v9h-7Z', tone: 'brass', v: 0 },
      { d: 'M66 54h20v10h-9v11h-11Z', tone: 'brass', v: 1 },
      { d: 'M50 48.5h32', tone: 'shine', w: 1.6 },
      { d: 'M14 44a12 12 0 0 1 8-7', tone: 'shine', w: 2 },
    ],
  },
  teapot: {
    en: 'teapot',
    no: 'tekanne',
    paint: '#3f86e8',
    asym: true,
    parts: [
      { d: 'M28 64C18 62 13 52 9 40', tone: 'paint', w: 7, v: 0 },
      { d: 'M28 64L10 58', tone: 'paint', w: 8, v: 1 },
      { d: 'M73 46C90 42 93 70 71 72', tone: 'paint', w: 6 },
      { d: 'M24 60C24 42 38 34 50 34C62 34 76 42 76 60C76 74 64 82 50 82C36 82 24 74 24 60Z', tone: 'paint' },
      { d: 'M36 80h28l-3 7h-22Z', tone: 'paint' },
      { d: 'M35 37C37 28 63 28 65 37Z', tone: 'paint' },
      { d: circle(50, 27, 5), tone: 'paint' },
      { d: 'M26 58C40 63 60 63 74 58', tone: 'paper', w: 2.6, lit: true },
      { d: 'M33 50C35 44 41 40 46 39', tone: 'shine', w: 3 },
    ],
  },
  boot: {
    en: 'boot',
    no: 'støvel',
    paint: '#b5652e',
    asym: true,
    parts: [
      { d: 'M52 14H74V80H18C11 80 9 71 16 67C24 62 40 60 52 56Z', tone: 'paint' },
      { d: 'M49 10H77V21H49Z', tone: 'dark' },
      { d: 'M11 80H77V88H11Z', tone: 'dark' },
      { d: 'M60 88H77V98H62Z', tone: 'dark', v: 1 },
      { d: 'M55 30h14M55 38h14M55 46h14', tone: 'paper', w: 2, lit: true },
      { d: 'M18 70C24 66 32 64 38 66', tone: 'dark', w: 2, lit: true },
      { d: 'M60 26V52', tone: 'shine', w: 3 },
    ],
  },
  lamp: {
    en: 'desk lamp',
    no: 'skrivebordslampe',
    paint: '#2e9e6a',
    asym: true,
    parts: [
      { d: 'M38 78L48 48', tone: 'metal', w: 5 },
      { d: 'M48 48L66 30', tone: 'metal', w: 5 },
      { d: circle(48, 48, 5), tone: 'metal' },
      { d: 'M16 88C16 79 25 76 38 76C51 76 60 79 60 88Z', tone: 'paint' },
      { d: circle(89, 51, 6), tone: 'flame', lit: true },
      { d: 'M58 27L70 16L96 40L78 56Z', tone: 'paint', v: 0 },
      { d: 'M60 30C58 12 88 10 96 36L78 50Z', tone: 'paint', v: 1 },
      { d: 'M66 22L86 40', tone: 'shine', w: 2.4 },
    ],
  },
  helmet: {
    en: 'helmet',
    no: 'hjelm',
    paint: '#e9eef2',
    asym: true,
    parts: [
      { d: 'M62 17l11-7l7 11l-11 7Z', tone: 'metal', v: 0 },
      { d: 'M64 24L80 5', tone: 'metal', w: 3, v: 1 },
      { d: circle(80, 5, 4), tone: 'flame', v: 1 },
      { d: 'M26 72H74V88H26Z', tone: 'metal' },
      { d: circle(50, 50, 30), tone: 'paint' },
      { d: 'M21 42C23 29 38 23 52 27V63C38 66 25 61 21 52Z', tone: 'glass' },
      { d: circle(71, 53, 6), tone: 'dark' },
      { d: 'M28 36C32 31 38 29 44 30', tone: 'shine', w: 3 },
    ],
  },
  telescope: {
    en: 'telescope',
    no: 'teleskop',
    asym: true,
    parts: [
      { d: 'M50 50L30 94M50 50L70 94M50 50L51 94', tone: 'wood', w: 3.6 },
      { d: 'M30 50.4L56 34.8L53 29.8L27 45.4Z', tone: 'dark', v: 0 },
      { d: 'M14 60L74 24L81 36L21 72Z', tone: 'brass' },
      { d: 'M70 21L79 15L89 32L80 38Z', tone: 'brass' },
      { d: 'M6 64L14 59L19 67L11 72Z', tone: 'dark' },
      { d: 'M45 46h10v10h-10Z', tone: 'dark' },
      { d: 'M22 61L70 32', tone: 'shine', w: 2 },
    ],
  },
  camera: {
    en: 'camera',
    no: 'kamera',
    paint: '#d64541',
    foot: 84,
    asym: true,
    parts: [
      { d: 'M84 26V40', tone: 'dark', w: 2.6, v: 1 },
      { d: 'M76 17C76 29 92 29 92 17Z', tone: 'metal', v: 1 },
      { d: 'M18 29h26v10h-26Z', tone: 'dark' },
      { d: 'M70 33h9v6h-9Z', tone: 'metal', v: 0 },
      { d: 'M14 38h72a6 6 0 0 1 6 6v34a6 6 0 0 1-6 6h-72a6 6 0 0 1-6-6v-34a6 6 0 0 1 6-6Z', tone: 'paint' },
      { d: 'M8 50h84v8h-84Z', tone: 'dark', lit: true },
      { d: circle(62, 61, 18), tone: 'metal' },
      { d: circle(62, 61, 10), tone: 'glass' },
      { d: 'M14 44h26', tone: 'shine', w: 2.5 },
      { d: 'M56 56a8 8 0 0 1 6-3', tone: 'shine', w: 2 },
    ],
  },
  can: {
    en: 'watering can',
    no: 'vannkanne',
    paint: '#45b36b',
    asym: true,
    parts: [
      { d: 'M44 44C44 18 84 16 86 48C86 58 82 64 76 66', tone: 'paint', w: 5 },
      { d: 'M38 74L12 42', tone: 'paint', w: 6 },
      { d: 'M3 42L11 31L21 40L13 51Z', tone: 'paint', v: 0 },
      { d: 'M34 46H76V80C76 85 72 88 67 88H43C38 88 34 85 34 80Z', tone: 'paint' },
      { d: 'M31 41H79V49H31Z', tone: 'paint' },
      { d: 'M34 60H76', tone: 'dark', w: 1.6, lit: true },
      { d: 'M41 54V80', tone: 'shine', w: 3 },
    ],
  },
  umbrella: {
    en: 'umbrella',
    no: 'paraply',
    paint: '#9a5de0',
    asym: true,
    parts: [
      { d: 'M48 5h4v10h-4Z', tone: 'dark' },
      { d: 'M50 48V84', tone: 'dark', w: 3 },
      { d: 'M50 84C50 95 37 95 37 86', tone: 'wood', w: 5, v: 0 },
      { d: 'M50 82V96', tone: 'wood', w: 7, v: 1 },
      { d: 'M9 52C11 28 31 14 50 14C69 14 89 28 91 52C85 47 77 47 70.5 52C64 47 56 47 50 52C44 47 36 47 29.5 52C23 47 15 47 9 52Z', tone: 'paint' },
      { d: 'M50 15C42 24 33 38 29.5 51M50 15C58 24 67 38 70.5 51', tone: 'dark', w: 1.6, lit: true },
      { d: 'M20 38C25 28 34 21 43 18', tone: 'shine', w: 3 },
    ],
  },
  radio: {
    en: 'radio',
    no: 'radio',
    paint: '#e8a13a',
    asym: true,
    parts: [
      { d: 'M80 42L93 8', tone: 'metal', w: 2.6, v: 0 },
      { d: circle(93, 8, 3), tone: 'metal', v: 0 },
      { d: 'M80 42V6', tone: 'metal', w: 2.6, v: 1 },
      { d: circle(80, 6, 3), tone: 'metal', v: 1 },
      { d: 'M28 42C28 28 52 28 52 42', tone: 'dark', w: 4 },
      { d: 'M18 40h64a8 8 0 0 1 8 8v30a8 8 0 0 1-8 8h-64a8 8 0 0 1-8-8v-30a8 8 0 0 1 8-8Z', tone: 'paint' },
      { d: circle(36, 63, 14), tone: 'dark' },
      { d: 'M27 58h18M25 63h22M27 68h18', tone: 'metal', w: 1.6, lit: true },
      { d: circle(68, 56, 8), tone: 'paper' },
      { d: 'M68 56L72 51', tone: 'dark', w: 1.6, lit: true },
      { d: circle(68, 76, 5), tone: 'metal' },
      { d: 'M16 47h28', tone: 'shine', w: 2 },
    ],
  },
  wrench: {
    en: 'spanner',
    no: 'fastnøkkel',
    rot: -35,
    foot: 82,
    asym: true,
    parts: [
      { d: 'M28 45H72V55H28Z', tone: 'metal' },
      { d: ring(79, 50, 13, 6.5), tone: 'metal', eo: true },
      { d: 'M9 36C13 29 26 29 32 38L34 45V55L32 62C26 71 13 71 9 64L20 58V42Z', tone: 'metal', v: 0 },
      { d: ring(21, 50, 13, 6.5), tone: 'metal', eo: true, v: 1 },
      { d: 'M32 47.5H66', tone: 'shine', w: 1.6 },
    ],
  },
  lantern: {
    en: 'lantern',
    no: 'lykt',
    paint: '#c83a32',
    asym: false,
    parts: [
      { d: 'M40 22C40 7 60 7 60 22', tone: 'metal', w: 3 },
      { d: 'M38 30C30 44 30 62 38 72H62C70 62 70 44 62 30Z', tone: 'glass' },
      { d: 'M50 42C43 52 45 62 50 64C55 62 57 52 50 42Z', tone: 'flame', v: 0 },
      { d: 'M45 30V72M55 30V72', tone: 'metal', w: 2 },
      { d: 'M33 31H67L58 19H42Z', tone: 'paint' },
      { d: 'M30 71H70V86H30Z', tone: 'paint' },
      { d: 'M36 40C35 48 35 56 37 64', tone: 'shine', w: 2.4 },
    ],
  },
  anchor: {
    en: 'anchor',
    no: 'anker',
    asym: false,
    parts: [
      { d: ring(50, 15, 8, 3.6), tone: 'metal', eo: true },
      { d: 'M29 26H71V34H29Z', tone: 'metal', v: 0 },
      { d: 'M46 22H54V86H46Z', tone: 'metal' },
      { d: 'M15 58C19 81 40 91 50 91C60 91 81 81 85 58L77 62C72 77 60 83 50 83C40 83 28 77 23 62Z', tone: 'metal' },
      { d: 'M9 61L22 51L27 67ZM91 61L78 51L73 67Z', tone: 'metal' },
      { d: 'M48.5 38V80', tone: 'shine', w: 1.6 },
    ],
  },
  mug: {
    en: 'mug',
    no: 'krus',
    paint: '#2bb5b0',
    asym: true,
    parts: [
      { d: 'M64 42C86 40 86 72 64 70', tone: 'paint', w: 7, v: 0 },
      { d: 'M64 38H80V74H64', tone: 'paint', w: 6, v: 1 },
      { d: 'M24 30H66V78C66 84 62 88 56 88H34C28 88 24 84 24 78Z', tone: 'paint' },
      { d: 'M24 46H66V53H24Z', tone: 'paper', lit: true },
      { d: 'M38 22C34 16 42 12 38 5M52 22C48 16 56 12 52 5', tone: 'shine', w: 2 },
      { d: 'M31 36V78', tone: 'shine', w: 3 },
    ],
  },
  bottle: {
    en: 'bottle',
    no: 'flaske',
    paint: '#45b36b',
    asym: false,
    parts: [
      { d: 'M45 5H55V16H45Z', tone: 'wood', v: 0 },
      { d: 'M42 9H58V18H42Z', tone: 'metal', v: 1 },
      { d: 'M36 46C36 36 44 32 44 24V15H56V24C56 32 64 36 64 46V86C64 89 62 91 59 91H41C38 91 36 89 36 86Z', tone: 'paint' },
      { d: 'M36 56H64V74H36Z', tone: 'paper' },
      { d: 'M42 48V84', tone: 'shine', w: 3 },
    ],
  },
  hourglass: {
    en: 'hourglass',
    no: 'timeglass',
    asym: false,
    parts: [
      { d: 'M29 17V83M71 17V83', tone: 'wood', w: 3.4 },
      { d: 'M35 17H65C65 36 54 44 52 50C54 56 65 64 65 83H35C35 64 46 56 48 50C46 44 35 36 35 17Z', tone: 'glass' },
      { d: 'M38 83C40 72 46 68 50 66C54 68 60 72 62 83Z', tone: 'brass', v: 0 },
      { d: 'M38 21H62C61 31 54 39 50 43C46 39 39 31 38 21Z', tone: 'brass', v: 1 },
      { d: 'M22 9H78V18H22ZM22 82H78V91H22Z', tone: 'wood' },
    ],
  },
  bulb: {
    en: 'light bulb',
    no: 'lyspære',
    asym: false,
    parts: [
      { d: 'M50 7C34 7 23 19 23 34C23 46 31 52 36 60V66H64V60C69 52 77 46 77 34C77 19 66 7 50 7Z', tone: 'glass' },
      { d: 'M42 62L44 42L48 48L52 40L56 48L58 42', tone: 'flame', w: 1.8, lit: true },
      { d: 'M35 65H65V73H35ZM37 73H63V80H37ZM39 80H61V87H39Z', tone: 'metal' },
      { d: 'M44 87H56L52 94H48Z', tone: 'dark' },
      { d: 'M32 30C33 21 40 15 46 14', tone: 'shine', w: 3 },
    ],
  },
  cog: {
    en: 'cog',
    no: 'tannhjul',
    asym: false,
    parts: [
      { d: cog(50, 50, 8, 31, 42, 11), tone: 'metal', eo: true, v: 0 },
      { d: cog(50, 50, 6, 31, 42, 11), tone: 'metal', eo: true, v: 1 },
      { d: ring(50, 50, 20, 17), tone: 'dark', eo: true, lit: true },
      { d: 'M28 34C32 27 38 23 44 21', tone: 'shine', w: 2.4 },
    ],
  },
  bolt: {
    en: 'bolt',
    no: 'bolt',
    asym: false,
    parts: [
      { d: 'M30 12H70L74 20V29L70 34H30L26 29V20Z', tone: 'metal' },
      { d: 'M42 12V34M58 12V34', tone: 'dark', w: 1.4, lit: true },
      { d: 'M23 34H77V41H23Z', tone: 'metal' },
      { d: 'M40 41H60V85L55 92H45L40 85Z', tone: 'metal' },
      { d: 'M40 51L60 47M40 59L60 55M40 67L60 63M40 75L60 71M40 83L60 79', tone: 'dark', w: 1.8, lit: true },
    ],
  },
  spring: {
    en: 'spring',
    no: 'fjær',
    asym: false,
    parts: [
      { d: 'M50 16C75 20 75 26 50 28C25 30 25 36 50 40C75 44 75 50 50 52C25 54 25 60 50 64C75 68 75 74 50 76C25 78 25 84 50 88', tone: 'metal', w: 5 },
      { d: 'M27 10H73V17H27ZM27 87H73V94H27Z', tone: 'dark' },
    ],
  },
  valve: {
    en: 'valve wheel',
    no: 'ventilratt',
    paint: '#e8473c',
    asym: false,
    parts: [
      { d: 'M45 38H55V74H45Z', tone: 'metal' },
      { d: 'M18 70H82V88H18Z', tone: 'metal' },
      { d: 'M12 65H24V93H12ZM76 65H88V93H76Z', tone: 'dark' },
      { d: 'M50 22V50M20 36H80', tone: 'paint', w: 4 },
      { d: `M18 36a32 17 0 1 0 64 0a32 17 0 1 0-64 0ZM26 36a24 11 0 1 1 48 0a24 11 0 1 1-48 0Z`, tone: 'paint', eo: true },
      { d: circle(50, 36, 5.5), tone: 'metal' },
    ],
  },
  battery: {
    en: 'battery',
    no: 'batteri',
    paint: '#f08a2c',
    asym: false,
    parts: [
      { d: 'M41 11H59V20H41Z', tone: 'metal' },
      { d: 'M35 19H65Q70 19 70 24V85Q70 90 65 90H35Q30 90 30 85V24Q30 19 35 19Z', tone: 'paint' },
      { d: 'M30 58H70V85Q70 90 65 90H35Q30 90 30 85Z', tone: 'dark' },
      { d: 'M50 30V46M42 38H58', tone: 'paper', w: 3.6, lit: true },
      { d: 'M36 25V80', tone: 'shine', w: 3 },
    ],
  },
  tree: {
    en: 'tree',
    no: 'tre',
    asym: false,
    parts: [
      { d: 'M44 92C46 78 46 66 44 56H56C54 66 54 78 56 92Z', tone: 'wood' },
      { d: 'M50 64L37 52M50 61L63 48', tone: 'wood', w: 4 },
      { d: 'M50 7C64 7 72 17 72 25C82 27 89 37 85 48C89 58 79 68 67 64C60 72 41 72 35 64C21 68 11 58 15 48C11 37 19 27 29 25C29 15 37 7 50 7Z', tone: 'leaf' },
      { d: `${circle(34, 40, 4)}${circle(60, 30, 4)}${circle(68, 52, 4)}`, tone: 'paint', v: 1, lit: true },
      { d: 'M31 23C36 16 43 13 50 13', tone: 'shine', w: 3 },
    ],
    paint: '#e8473c',
  },
  house: {
    en: 'house',
    no: 'hus',
    paint: '#d64541',
    asym: true,
    parts: [
      { d: 'M64 20H73V42L64 34Z', tone: 'dark', v: 0 },
      { d: 'M22 50H78V90H22Z', tone: 'paper' },
      { d: 'M12 53L50 19L88 53Z', tone: 'paint' },
      { d: 'M29 64H42V90H29Z', tone: 'wood' },
      { d: 'M52 60H70V77H52Z', tone: 'glass' },
      { d: 'M61 60V77M52 68.5H70', tone: 'paper', w: 1.6, lit: true },
      { d: 'M22 42L40 26', tone: 'shine', w: 2.4 },
    ],
  },
  boat: {
    en: 'sailing boat',
    no: 'seilbåt',
    paint: '#3f86e8',
    asym: true,
    parts: [
      { d: 'M50 12L63 16L50 21Z', tone: 'flame', v: 1 },
      { d: 'M50 12V71', tone: 'wood', w: 3 },
      { d: 'M53 16C67 30 75 50 77 66H53Z', tone: 'paper' },
      { d: 'M47 24C40 38 33 54 25 66H47Z', tone: 'paper' },
      { d: 'M9 70H91L79 88H23Z', tone: 'paint' },
      { d: 'M14 76H86', tone: 'paper', w: 2, lit: true },
      { d: 'M57 24C64 34 69 46 71 58', tone: 'shine', w: 2 },
    ],
  },
  fish: {
    en: 'fish',
    no: 'fisk',
    paint: '#f08a2c',
    foot: 68,
    asym: true,
    parts: [
      { d: 'M68 50L91 32L86 50L91 68Z', tone: 'paint', v: 0 },
      { d: 'M68 50L92 36V64Z', tone: 'paint', v: 1 },
      { d: 'M38 37C42 25 55 25 60 39Z', tone: 'paint' },
      { d: 'M12 50C23 34 46 30 64 42C68 44 70 46 72 48V52C70 54 68 56 64 58C46 70 23 66 12 50Z', tone: 'paint' },
      { d: circle(25, 47, 3.6), tone: 'dark' },
      { d: 'M34 40C38 46 38 54 34 60', tone: 'dark', w: 2, lit: true },
      { d: 'M23 42C31 36 41 34 50 36', tone: 'shine', w: 2.6 },
    ],
  },
  flower: {
    en: 'flower',
    no: 'blomst',
    paint: '#e060a8',
    asym: true,
    parts: [
      { d: 'M50 66V36', tone: 'leaf', w: 3.4 },
      { d: 'M50 58C40 56 33 50 30 43C41 42 48 48 50 58Z', tone: 'leaf', v: 0 },
      { d: 'M50 58C60 56 67 50 70 43C59 42 52 48 50 58Z', tone: 'leaf', v: 1 },
      { d: petals(50, 28, 6, 11, 8.5), tone: 'paint' },
      { d: circle(50, 28, 7), tone: 'brass' },
      { d: 'M31 66H69V73H31Z', tone: 'wood' },
      { d: 'M34 72H66L61 93H39Z', tone: 'wood' },
    ],
  },
  apple: {
    en: 'apple',
    no: 'eple',
    paint: '#e8473c',
    asym: true,
    parts: [
      { d: 'M50 30C50 22 52 16 56 11', tone: 'wood', w: 3 },
      { d: 'M55 18C61 9 71 9 76 13C70 22 61 22 55 18Z', tone: 'leaf', v: 0 },
      { d: 'M50 30C58 22 80 22 82 46C84 70 66 88 56 86C52 85 48 85 44 86C34 88 16 70 18 46C20 22 42 22 50 30Z', tone: 'paint' },
      { d: 'M28 42C30 34 36 30 42 30', tone: 'shine', w: 3 },
    ],
  },
  bird: {
    en: 'bird',
    no: 'fugl',
    paint: '#3f86e8',
    asym: true,
    parts: [
      { d: 'M60 30L64 16L69 30Z', tone: 'paint', v: 1 },
      { d: 'M44 74V88M52 74V88', tone: 'brass', w: 2.2 },
      { d: 'M25 56L5 46L9 64Z', tone: 'paint' },
      { d: 'M20 58C20 44 34 38 50 42C62 44 68 52 66 62C62 72 44 76 32 72C24 70 20 64 20 58Z', tone: 'paint' },
      { d: circle(64, 40, 12), tone: 'paint' },
      { d: 'M75 36L89 41L75 46Z', tone: 'brass' },
      { d: circle(67, 37, 2.6), tone: 'dark' },
      { d: 'M28 54C38 46 53 48 58 58C47 63 35 62 28 54Z', tone: 'dark', lit: true },
      { d: 'M56 34C58 31 61 30 64 30', tone: 'shine', w: 2 },
    ],
  },
  clock: {
    en: 'alarm clock',
    no: 'vekkerklokke',
    paint: '#e8473c',
    asym: false,
    parts: [
      { d: 'M31 77L23 90M69 77L77 90', tone: 'dark', w: 4 },
      { d: 'M50 22V15', tone: 'dark', w: 3 },
      { d: 'M19 27C22 15 36 13 40 22ZM60 22C64 13 78 15 81 27Z', tone: 'brass' },
      { d: circle(50, 52, 30), tone: 'paint' },
      { d: circle(50, 52, 23), tone: 'paper' },
      { d: 'M50 52V35M50 52L62 59', tone: 'dark', w: 3, v: 0 },
      { d: 'M50 52V35M50 52L37 60', tone: 'dark', w: 3, v: 1 },
      { d: 'M30 36C34 30 40 27 46 26', tone: 'shine', w: 2.6 },
    ],
  },
} as const satisfies Record<string, ThingDef>;

export type ThingId = keyof typeof THINGS;
export const THING_IDS = Object.keys(THINGS) as ThingId[];

export const thingDef = (id: ThingId): ThingDef => THINGS[id];

/** The parts drawn for the base form or the variant. */
export function partsOf(id: ThingId, variant: boolean): readonly Part[] {
  const v = variant ? 1 : 0;
  return thingDef(id).parts.filter((p) => p.v === undefined || p.v === v);
}

/** Has a variant with one part different. */
export function hasVariant(id: ThingId): boolean {
  return thingDef(id).parts.some((p) => p.v !== undefined);
}

/** A variant that changes the outline too, not only a detail inside it. */
export function hasShadowVariant(id: ThingId): boolean {
  return thingDef(id).parts.some((p) => p.v !== undefined && !p.lit);
}

/** Has parts in its own colour, which can be repainted. */
export function isPaintable(id: ThingId): boolean {
  return thingDef(id).parts.some((p) => p.tone === 'paint' && !p.lit);
}

/**
 * The colours a thing on WHAT CHANGED?'s shelf is painted: the kit's six (kit/tokens.ts
 * `PALETTE`), the same in every game. Two of them are blues; a new colour is never the other blue
 * (`paintGap`).
 */
export const PAINTS: readonly string[] = PALETTE;

/** How far apart two colours are, 0 (the same) to about 440 (black and white). */
export function paintGap(a: string, b: string): number {
  const x = rgb(a);
  const y = rgb(b);
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

/**
 * A card coming onto the board, 0 to 1: it grows past its size and settles, and fades in on the
 * way. Drawn from a value in the view state, never from a CSS animation.
 */
export function pop(f: number): { scale: number; opacity: number } {
  const v = Math.max(0, Math.min(1, f));
  return { scale: v < 0.6 ? 0.4 + 0.75 * (v / 0.6) : 1.15 - 0.15 * ((v - 0.6) / 0.4), opacity: Math.min(1, v / 0.6) };
}

/** Turning over, 0 to 1, eased at both ends. */
export const ease = (f: number) => {
  const v = Math.max(0, Math.min(1, f));
  return v * v * (3 - 2 * v);
};

/** A random order, from a seeded random. */
export function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** `n` different items, picked at random. */
export function pick<T>(items: readonly T[], n: number, rand: () => number): T[] {
  return shuffle(items, rand).slice(0, n);
}

function rgb(c: string): [number, number, number] {
  const n = parseInt(c.slice(1, 7), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `a` blended towards `b` by `t`. */
export function mix(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  return `#${x.map((v, i) => Math.round(v + (y[i]! - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

const TONES: Record<Exclude<Tone, 'paint' | 'shine'>, readonly [string, string, string]> = {
  metal: ['#f1f5f8', '#a9b6bf', '#4f5c66'],
  brass: ['#ffe8a6', '#d6a23f', '#7c561c'],
  wood: ['#dca068', '#945a2a', '#4f2d12'],
  glass: ['#eefaff', '#9fd2ee', '#4f86a8'],
  dark: ['#56616c', '#2a3139', '#11161b'],
  leaf: ['#c4ec93', '#5fa43e', '#2c5a1c'],
  paper: ['#fffdf6', '#efe5cc', '#bfae8c'],
  flame: ['#fffbe0', '#ffd15a', '#ff8a2a'],
};

const OUTLINE = 'rgba(6,8,12,.62)';

export interface ThingProps {
  thing: ThingId;
  /** Its colour, in place of its own. */
  paint?: string | undefined;
  /** Lit in its own colours, or a flat silhouette in `ink`. */
  mode?: 'lit' | 'shadow';
  variant?: boolean;
  mirror?: boolean;
  ink?: string;
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
}

/** One thing, as its own SVG: on a page, or nested in a bigger drawing with x, y, width, height. */
export const ThingSvg = memo(function ThingSvg({
  thing,
  paint,
  mode = 'lit',
  variant = false,
  mirror = false,
  ink = '#0b0f14',
  x,
  y,
  width = '100%',
  height = '100%',
  style,
}: ThingProps) {
  const uid = `t${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const def = thingDef(thing);
  const lit = mode === 'lit';
  const colour = paint ?? def.paint ?? '#a9b6bf';
  const parts = partsOf(thing, variant).filter((p) => lit || (!p.lit && p.tone !== 'shine'));
  const tones = lit ? [...new Set(parts.map((p) => p.tone))].filter((t): t is Exclude<Tone, 'shine'> => t !== 'shine') : [];
  const stops = (t: Exclude<Tone, 'shine'>): readonly [string, string, string] =>
    t === 'paint' ? [mix(colour, '#ffffff', 0.45), colour, mix(colour, '#000000', 0.5)] : TONES[t];
  const turn = [mirror ? 'translate(100 0) scale(-1 1)' : '', def.rot ? `rotate(${def.rot} 50 50)` : ''].join(' ').trim();

  return (
    <svg viewBox="0 0 100 100" x={x} y={y} width={width} height={height} style={style} overflow="visible" aria-hidden="true">
      {tones.length > 0 && (
        <defs>
          {tones.map((t) => {
            const s = stops(t);
            const trio = [
              <stop key="a" offset="0" stopColor={s[0]} />,
              <stop key="b" offset=".5" stopColor={s[1]} />,
              <stop key="c" offset="1" stopColor={s[2]} />,
            ];
            return (
              <Fragment key={t}>
                <linearGradient id={`${uid}${t}`} x1=".15" y1="0" x2=".85" y2="1">
                  {trio}
                </linearGradient>
                <linearGradient id={`${uid}${t}u`} gradientUnits="userSpaceOnUse" x1="15" y1="5" x2="85" y2="95">
                  {trio}
                </linearGradient>
              </Fragment>
            );
          })}
        </defs>
      )}
      <g transform={turn || undefined}>
        {parts.map((p, i) => {
          if (p.tone === 'shine') {
            return p.w ? (
              <path key={i} d={p.d} fill="none" stroke="#ffffff" strokeWidth={p.w} strokeLinecap="round" opacity=".5" />
            ) : (
              <path key={i} d={p.d} fill="#ffffff" opacity=".4" />
            );
          }
          if (p.w) {
            const paintStroke = lit ? `url(#${uid}${p.tone}u)` : ink;
            return (
              <g key={i}>
                {lit && p.w >= 2.5 && (
                  <path d={p.d} fill="none" stroke={OUTLINE} strokeWidth={p.w + 2.6} strokeLinecap="round" strokeLinejoin="round" />
                )}
                <path d={p.d} fill="none" stroke={paintStroke} strokeWidth={p.w} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          }
          return (
            <path
              key={i}
              d={p.d}
              fill={lit ? `url(#${uid}${p.tone})` : ink}
              fillRule={p.eo ? 'evenodd' : 'nonzero'}
              stroke={lit ? OUTLINE : ink}
              strokeWidth={lit ? 1.5 : 1}
              strokeLinejoin="round"
            />
          );
        })}
      </g>
    </svg>
  );
});

/** The card and label colours of the archive in each era: pale paper that reads in low light. */
export function paperOf(era: Era): { top: string; bottom: string; edge: string } {
  const tint: Record<Era, string> = {
    stars: '#cfe6ff',
    neon: '#f4d4ff',
    debris: '#ffd9cc',
    dust: '#f3dcb4',
    canopy: '#dcf2c8',
  };
  return { top: mix(tint[era], '#ffffff', 0.55), bottom: tint[era], edge: mix(tint[era], '#000000', 0.45) };
}
